import type { JurisdictionRow } from '@mollusk/shared';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { buildJurisdictionsCsv } from './csv';
import {
  COLUMNS,
  COLUMN_GROUP_LABELS,
  type ColumnDef,
  type ColumnGroup,
  EMPTY_FILTERS,
  type JurisdictionFilters,
  type SortDir,
  columnValue,
  defaultVisibleColumns,
  distinctValues,
  filterJurisdictions,
  sortByColumn,
  sortedCountEntries,
} from './tableLogic';

const GROUP_ORDER: ColumnGroup[] = [
  'core',
  'activity',
  'geography',
  'quality',
  'status',
  'freshness',
];

/** True when the row carries any trade or tag breakdown to expand. */
function hasBreakdown(r: JurisdictionRow): boolean {
  return Object.keys(r.trade_counts).length > 0 || Object.keys(r.tag_counts).length > 0;
}

/** Download `content` as a file, client-side (no server round-trip). */
function downloadTextFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Chip row of `label count` pairs, busiest first, for the expanded detail panel. */
function CountChips({ counts }: { counts: Readonly<Record<string, number>> }) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {sortedCountEntries(counts).map(([value, n]) => (
        <Chip
          key={value}
          size="small"
          variant="outlined"
          label={`${value} ${n.toLocaleString()}`}
        />
      ))}
    </Stack>
  );
}

const KIND_LABELS: Record<JurisdictionRow['kind'], string> = {
  place: 'Place',
  county: 'County',
};

function formatCell(row: JurisdictionRow, col: ColumnDef): string {
  const v = columnValue(row, col);
  if (v === null || v === undefined) return '—';
  if (col.id === 'kind') return KIND_LABELS[row.kind];
  switch (col.kind) {
    case 'pct':
      return `${(Number(v) * 100).toFixed(1)}%`;
    case 'int':
      return Number(v).toLocaleString();
    case 'num':
      return Number(v).toLocaleString();
    case 'bool':
      return v ? 'Yes' : 'No';
    default:
      return String(v);
  }
}

export function JurisdictionsTable({ rows }: { rows: readonly JurisdictionRow[] }) {
  const [filters, setFilters] = useState<JurisdictionFilters>(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState<keyof JurisdictionRow>('permit_count');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [visible, setVisible] = useState<Set<keyof JurisdictionRow>>(defaultVisibleColumns);
  const [colAnchor, setColAnchor] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const states = useMemo(() => distinctValues(rows, 'state'), [rows]);
  const grades = useMemo(() => distinctValues(rows, 'grade'), [rows]);

  const sortCol = useMemo(() => COLUMNS.find((c) => c.id === sortKey) ?? COLUMNS[0], [sortKey]);
  const filtered = useMemo(() => filterJurisdictions(rows, filters), [rows, filters]);
  const sorted = useMemo(
    () => (sortCol ? sortByColumn(filtered, sortCol, sortDir) : filtered),
    [filtered, sortCol, sortDir],
  );
  const paged = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage],
  );

  const visibleColumns = useMemo(() => COLUMNS.filter((c) => visible.has(c.id)), [visible]);

  function patchFilters(patch: Partial<JurisdictionFilters>) {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(0);
  }

  function onSort(key: keyof JurisdictionRow) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function toggleColumn(id: keyof JurisdictionRow) {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Export every jurisdiction with every column (not just the filtered view or
  // the visible columns) — the CSV always "gets everything". Built off `rows`.
  function downloadCsv() {
    downloadTextFile(
      'mollusk-permit-detail-jurisdictions.csv',
      buildJurisdictionsCsv(rows),
      'text/csv;charset=utf-8',
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}
      >
        <TextField
          size="small"
          label="Search jurisdiction"
          value={filters.search}
          onChange={(e) => patchFilters({ search: e.target.value })}
        />
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>State</InputLabel>
          <Select
            label="State"
            value={filters.state ?? ''}
            onChange={(e) => patchFilters({ state: e.target.value || null })}
          >
            <MenuItem value="">All</MenuItem>
            {states.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Grade</InputLabel>
          <Select
            label="Grade"
            value={filters.grade ?? ''}
            onChange={(e) => patchFilters({ grade: e.target.value || null })}
          >
            <MenuItem value="">All</MenuItem>
            {grades.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={filters.kind ?? ''}
            onChange={(e) =>
              patchFilters({ kind: (e.target.value || null) as JurisdictionFilters['kind'] })
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="place">Places</MenuItem>
            <MenuItem value="county">Counties</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.coveredOnly}
              onChange={(e) => patchFilters({ coveredOnly: e.target.checked })}
            />
          }
          label="Covered only"
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={downloadCsv}>
          Download CSV
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewColumnIcon />}
          onClick={(e) => setColAnchor(e.currentTarget)}
        >
          Columns
        </Button>
        <Menu anchorEl={colAnchor} open={!!colAnchor} onClose={() => setColAnchor(null)}>
          {GROUP_ORDER.flatMap((group) => [
            <ListSubheader key={`h-${group}`}>{COLUMN_GROUP_LABELS[group]}</ListSubheader>,
            ...COLUMNS.filter((c) => c.group === group).map((c) => (
              <MenuItem key={c.id} dense onClick={() => toggleColumn(c.id)}>
                <Checkbox edge="start" size="small" checked={visible.has(c.id)} tabIndex={-1} />
                <ListItemText primary={c.label} />
              </MenuItem>
            )),
          ])}
        </Menu>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {filtered.length.toLocaleString()} jurisdictions
        {filtered.length !== rows.length ? ` (of ${rows.length.toLocaleString()})` : ''}. Click a
        covered jurisdiction's name for a random permit, or the ▸ toggle to see its trade &amp; tag
        breakdown. Download CSV exports every column for every jurisdiction.
      </Typography>

      <TableContainer component={Paper} sx={{ maxHeight: 620 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 36, p: 0 }} />
              {visibleColumns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.numeric ? 'right' : 'left'}
                  sortDirection={sortKey === col.id ? sortDir : false}
                >
                  <TableSortLabel
                    active={sortKey === col.id}
                    direction={sortKey === col.id ? sortDir : 'asc'}
                    onClick={() => onSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((row) => {
              const expandable = hasBreakdown(row);
              const isOpen = expanded.has(row.id);
              return [
                <TableRow key={row.id} hover>
                  <TableCell sx={{ p: 0 }}>
                    {expandable ? (
                      <IconButton
                        size="small"
                        aria-label={isOpen ? 'Hide trades & tags' : 'Show trades & tags'}
                        onClick={() => toggleExpanded(row.id)}
                      >
                        {isOpen ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    ) : null}
                  </TableCell>
                  {visibleColumns.map((col) => {
                    if (col.id === 'jurisdiction_name') {
                      return (
                        <TableCell key={col.id}>
                          {row.jurisdiction_name}
                          {row.kind === 'county' ? (
                            <Chip
                              label="County (unincorporated)"
                              size="small"
                              variant="outlined"
                              sx={{ ml: 1, height: 18, fontSize: 11 }}
                            />
                          ) : null}
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={col.id} align={col.numeric ? 'right' : 'left'}>
                        {formatCell(row, col)}
                      </TableCell>
                    );
                  })}
                </TableRow>,
                <TableRow key={`${row.id}-detail`}>
                  <TableCell sx={{ py: 0, borderBottom: 0 }} colSpan={visibleColumns.length + 1}>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 1.5, pl: 4 }}>
                        {Object.keys(row.trade_counts).length > 0 ? (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Trades (permits carrying each system)
                            </Typography>
                            <CountChips counts={row.trade_counts} />
                          </Box>
                        ) : null}
                        {Object.keys(row.tag_counts).length > 0 ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Tags (permits carrying each keyword)
                            </Typography>
                            <CountChips counts={row.tag_counts} />
                          </Box>
                        ) : null}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>,
              ];
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sorted.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Box>
  );
}
